<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Clientes</h1>
      <v-btn color="primary" @click="handleCreateClient">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Cliente
      </v-btn>
    </div>

    <!-- Tabela -->
    <v-card class="elevation-4">
      <v-card-text>
        <!-- Filtro -->
        <v-row class="mb-6" dense>
          <v-col cols="12" md="8">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar cliente" clearable hide-details
              prepend-inner-icon="mdi-magnify" />
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table-server :headers="columns" :items="clients" :items-length="totalItems" :loading="isLoading"
          :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage" class="elevation-0"
          @update:page="page = $event" @update:items-per-page="itemsPerPage = $event"
          @update:options="handleUpdateOptions">

          <template #item.name="{ item }">
            <span>{{ item.name }}</span>
          </template>

          <template #item.email="{ item }">
            <span>{{ item.email }}</span>
          </template>

          <template #item.phone="{ item }">
            <span>{{ formatPhone(item.phone) }}</span>
          </template>

          <template #item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template #item.actions="{ item }">
            <v-icon icon="mdi-pencil" color="warning" class="mr-2" @click="handleEdit(item)" title="Editar" />
          </template>
        </v-data-table-server>

        <!-- Paginação -->
        <div class="d-flex justify-center mt-4">
          <v-pagination v-model="page" :length="pageCount" rounded="circle" color="primary" />
        </div>

        <!-- Erro -->
        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/api";
import { useToastStore } from "@/store/toast";
import { useLoadingOverlay } from "@/store/loadingOverlay";

const router = useRouter();
const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();

const clients = ref([]);
const isLoading = ref(true);
const hasError = ref(false);
const searchTerm = ref("");

// paginação
const page = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);
const sortBy = ref(null);
const sortDesc = ref(false);

const isActiveTagMap = {
  0: "error",
  1: "success",
};


const noDataMessage = computed(() => {
  if (isLoading.value) return "Carregando...";
  return "";
});

const columns = [
  { title: "Nome", key: "name", sortable: true },
  { title: "Telefone", key: "phone", sortable: true },
  { title: "E-mail", key: "email", sortable: true },
  { title: "Situação", key: "is_active", sortable: true },
  { title: "Ações", key: "actions", sortable: false },
];

const loadClients = async () => {
  isLoading.value = true;
  hasError.value = false;
  loadingOverlay.startLoading();
  try {
    const params = {
      page: page.value,
      per_page: itemsPerPage.value,
      search: searchTerm.value,
      sort_by: sortBy.value,
      sort_desc: sortDesc.value ? 1 : 0,
    };

    const response = await api.get("/clients", { params });

    clients.value = response.data.data;
    totalItems.value = response.data.total;
    itemsPerPage.value = response.data.per_page;
    page.value = response.data.current_page;

  } catch (error) {
    console.error("Erro ao carregar clientes:", error);
    hasError.value = true;
    toastStore.showToast({
      message: error?.response?.data?.error || "Ocorreu um erro ao carregar a lista de clientes.",
      color: "error",
    });
  } finally {
    isLoading.value = false;
    loadingOverlay.stopLoading();
  }
};

watch([page, itemsPerPage], () => {
  loadClients();
});

// Adiciona o watch para o termo de busca (que deve resetar a página para 1)
watch(searchTerm, () => {
  page.value = 1; // Sempre volta para a primeira página ao pesquisar
  loadClients();
});

const handleUpdateOptions = (options) => {
  if (options.sortBy.length) {
    sortBy.value = options.sortBy[0].key;
    sortDesc.value = options.sortBy[0].order === 'desc';
  } else {
    sortBy.value = null;
    sortDesc.value = false;
  }
  loadClients();
}

const handleCreateClient = () => {
  router.push("/app/clientes/novo");
};

const handleEdit = (row) => {
  router.push({ name: "client-edit", params: { id: row.id } });
};

function formatIsActive(is_active) {
  switch (is_active) {
    case 0:
      return "Desativado";
    case 1:
      return "Ativo";
    default:
      return is_active;
  }
}

function formatPhone(phone) {
  if (!phone) return "";

  const cleaned = ("" + phone).replace(/\D/g, "");

  const match11 = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
  if (match11) return `(${match11[1]}) ${match11[2]}-${match11[3]}`;

  const match10 = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
  if (match10) return `(${match10[1]}) ${match10[2]}-${match10[3]}`;

  return phone;
}

onMounted(() => {
  loadClients();
});
</script>
